import { notification, Popconfirm } from 'antd';
import Badge from 'components/Badge';
import {
  resetFilters,
  setPrint,
  setShowDetails,
  setShowDimensions,
  setShowFilterPopup,
  setShowTerrain,
} from 'features/siteMapSlice';
import DetailsIcon from 'lib/assets/details.svg';
import DimensionsIcon from 'lib/assets/dimensions.svg';
import FilterIcon from 'lib/assets/filter.svg';
import PrintIcon from 'lib/assets/print.svg';
import ResetIcon from 'lib/assets/reset.svg';
import TerrainIcon from 'lib/assets/terrain.svg';
import gaFunction from 'lib/ga';
import { useSiteMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import {
  displayDetails,
  displayDimensions,
  hideDetails,
  hideDimensions,
  resetFilterSVG,
} from 'lib/utils';
import { useRef, useState } from 'react';
import Draggable from 'react-draggable';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';
import FilterMenuPopup from './popup/FilterMenuPopup';

interface Props {
  resetTransform: () => void;
  partnerHidePrint: boolean;
}

const Nav = ({ resetTransform, partnerHidePrint }: Props) => {
  // Redux Hooks and States
  const dispatch = useAppDispatch();

  const { totalSelectedFilters, showFilterPopup, showDetails, showDimensions, showTerrain } =
    useAppSelector((state) => ({
      totalSelectedFilters: state.siteMap.totalSelectedFilters,
      showFilterPopup: state.siteMap.showFilterPopup,
      showDetails: state.siteMap.showDetails,
      showDimensions: state.siteMap.showDimensions,
      showTerrain: state.siteMap.showTerrain,
    }));

  // Get Site Map Data from React-Query Cache (No Params)
  const siteMapData = useSiteMapQuery().data!;
  // Semantic Vars From Site Map Data
  const {
    displayOptions: [displayOptions],
  } = siteMapData;

  // Constants
  const URLParams = new URLSearchParams(window.location.search);
  const filterPlanSysId = URLParams.get('filterPlanSysId');

  const windowWidth = useWindowDimensions().width;
  const [navPositionX, setNavPositionX] = useState(windowWidth);
  const ref = useRef<HTMLDivElement>(null);

  // Functions
  const handleNavClick = (clickedNav: string) => {
    switch (clickedNav) {
      case 'filter':
        dispatch(setShowFilterPopup(true));
        break;
      case 'details':
        if (!localStorage.getItem('showedDetailsNotification')) {
          notification.info({
            message: 'Zoom closer to see details.',
            duration: 3,
            className: 'cy-notification',
          });
          localStorage.setItem('showedDetailsNotification', 'true');
        }
        if (showDetails) {
          hideDetails();
          dispatch(setShowDetails(false));
          if (!displayOptions.hideDimensions && showDimensions) {
            hideDimensions();
            dispatch(setShowDimensions(false));
          }
        } else {
          displayDetails();
          dispatch(setShowDetails(true));
          gaFunction.spShowDetails();
        }
        break;
      case 'dimensions':
        if (!localStorage.getItem('showedDimsNotification')) {
          notification.info({
            message: 'Zoom closer to see dimensions.',
            duration: 3,
            className: 'cy-notification',
          });
          localStorage.setItem('showedDimsNotification', 'true');
        }
        if (showDimensions) {
          hideDimensions();
          dispatch(setShowDimensions(false));
        } else {
          displayDimensions();
          dispatch(setShowDimensions(true));
          gaFunction.spShowDimensions();
          if (!displayOptions.hideDetails && !showDetails) {
            displayDetails();
            dispatch(setShowDetails(true));
            gaFunction.spShowDetails();
          }
        }
        break;

      case 'terrain':
        if (showTerrain) {
          dispatch(setShowTerrain(false));
        } else {
          dispatch(setShowTerrain(true));
          //gaFunction?.spshowTerrain();
        }
        break;

      case 'reset':
        if (showDetails) {
          hideDetails();
          dispatch(setShowDetails(false));
        }
        if (showDimensions) {
          hideDimensions();
          dispatch(setShowDimensions(false));
        }
        resetTransform();
        gaFunction.spReset();
        break;
      case 'print':
        dispatch(setPrint(true));
        break;
    }
  };

  const handleReset = () => {
    resetFilterSVG(siteMapData);
    dispatch(resetFilters());
  };

  return (
    <Draggable
      handle=".handle"
      bounds="parent"
      // get nav x-position for filterMenuPopup responsiveness
      onStop={() => {
        setNavPositionX(ref.current!.getBoundingClientRect().x);
      }}
    >
      <StyledNav data-cy="nav-bar" ref={ref}>
        <div className="handle" data-cy="nav-handle" />
        <StyledNavButton
          data-cy="filter-button"
          active={showFilterPopup}
          className={`${showFilterPopup ? 'pe-none' : ''}`}
          onClick={() => handleNavClick('filter')}
        >
          {totalSelectedFilters > 0 && <Badge text={totalSelectedFilters} />}
          <SVG data-cy="filter-icon" src={FilterIcon} />
          <StyledNavButtonTitle>Filter</StyledNavButtonTitle>
        </StyledNavButton>
        {showFilterPopup && <FilterMenuPopup navPositionX={navPositionX} />}
        {!displayOptions.hideDetails && (
          <StyledNavButton
            data-cy="details-button"
            active={showDetails}
            onClick={() => handleNavClick('details')}
          >
            <SVG data-cy="details-icon" src={DetailsIcon} />
            <StyledNavButtonTitle>Details</StyledNavButtonTitle>
          </StyledNavButton>
        )}
        {!displayOptions.hideDimensions && (
          <StyledNavButton
            data-cy="dimensions-button"
            active={showDimensions}
            onClick={() => handleNavClick('dimensions')}
          >
            <SVG data-cy="dimensions-icon" src={DimensionsIcon} />
            <StyledNavButtonTitle>Dimensions</StyledNavButtonTitle>
          </StyledNavButton>
        )}
        {!displayOptions.hideTerrain && (
          <StyledNavButton
            data-cy="terrain-button"
            active={showTerrain}
            onClick={() => handleNavClick('terrain')}
          >
            <SVG data-cy="terrain-icon" src={TerrainIcon} />
            <StyledNavButtonTitle>Terrain</StyledNavButtonTitle>
          </StyledNavButton>
        )}
        {!partnerHidePrint && !displayOptions.hidePrintButton && (
          <StyledNavButton onClick={() => handleNavClick('print')}>
            <SVG data-cy="print-icon" src={PrintIcon} />
            <StyledNavButtonTitle>Print</StyledNavButtonTitle>
          </StyledNavButton>
        )}
        {!filterPlanSysId && (
          <>
            {totalSelectedFilters > 0 ? (
              <Popconfirm
                title="Are you sure you want to reset filters?"
                placement="left"
                onConfirm={handleReset}
                okText="Yes"
                cancelText="No"
              >
                <StyledResetButton data-cy="reset-button" onClick={() => handleNavClick('reset')}>
                  <SVG data-cy="reset-icon" src={ResetIcon} />
                  <StyledNavButtonTitle>Reset</StyledNavButtonTitle>
                </StyledResetButton>
              </Popconfirm>
            ) : (
              <StyledResetButton data-cy="reset-button" onClick={() => handleNavClick('reset')}>
                <SVG data-cy="reset-icon" src={ResetIcon} />
                <StyledNavButtonTitle>Reset</StyledNavButtonTitle>
              </StyledResetButton>
            )}
          </>
        )}
      </StyledNav>
    </Draggable>
  );
};

export default Nav;

const StyledNav = styled.nav`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 0;
  top: 25px;
  border-radius: 10px;
  background-color: #ffffff;
  z-index: 200;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 0px 10px 3px;
  .handle {
    width: 100%;
    height: 1.5vh;
    background-color: ${({ theme }) => theme.colors.primaryColor};
    cursor: grab;
    border-radius: 1vh 1vh 0 0;

    &:active {
      cursor: grabbing;
    }
  }
`;

const StyledNavButton = styled.button<{
  active?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 81px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  overflow: hidden;
  &:hover {
    background-color: gainsboro;
  }
  svg {
    height: 100%;
    width: 100%;
    path,
    rect,
    path,
    circle {
      fill: black;
    }
  }

  [data-cy='reset-icon'] {
    path,
    rect,
    circle {
      fill: none;
    }
  }

  ${({ active }) =>
    active &&
    `
    background-color: gainsboro;
    color: black;
    `}
`;

const StyledNavButtonTitle = styled.span`
  position: relative;
  bottom: 10%;
  font-size: 0.82rem;
`;

const StyledResetButton = styled(StyledNavButton)`
  border-radius: 0 0 10px 10px;
`;
